import React, {useContext, useEffect, useState} from 'react';
import {Card, Divider, Tooltip} from "antd";
import axios from "axios";
import {ScheduleRounded, ShieldRounded} from "@mui/icons-material";
import CircularLoader from "../../common/CircularLoader";
import {useSearchParams} from "wouter";
import UserSelector from "../components/UserSelector";
import Tabs from "../../common/Tabs";
import Tab from "../../common/Tab";
import {NavigationContext} from "../../common/NavigationContext";
import RemoveUserFromClassButton from "../components/RemoveUserFromClassButton";

const ManageClassUsersDrawer = () => {
    const TABS = {
        EXISTING_USERS: { val: 0, label: 'Existing Users'},
        ADD_USERS: { val: 1, label: 'Add Users'}
    };

    const [loading, setLoading] = useState(true);
    const [selectedClass, setSelectedClass] = useState(null);
    const [users, setUsers] = useState({});
    const [allUsers, setAllUsers] = useState([]);
    const [classStudents, setClassStudents] = useState([]);
    const [classTeachers, setClassTeachers] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [classId, setClassId] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [activeTabKey, setActiveTabKey] = useState(TABS.EXISTING_USERS.val);
    const { setDrawerVisible } = useContext(NavigationContext);

    // Get classId from search parameters
    useEffect(() => {
        const editClassId = searchParams.get('editClassId');
        if (editClassId) {
            setClassId(parseInt(editClassId, 10));
        }
    }, [searchParams]);

    useEffect(() => {
        axios.get('/web/ui/sc/classes')
            .then(response => {
                const data = response.data;
                const classesArray = Object.values(data);

                // Set the default selected class based on classId from search params
                if (classId) {
                    const defaultClass = classesArray.find(cls => cls.classId === classId);
                    if (defaultClass) {
                        setSelectedClass(defaultClass);
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching school classes:', error);
            });

        axios.get('/web/ui/sc/users')
            .then(response => {
                const usersObj = response.data;
                const usersArray = Object.values(usersObj);
                setUsers(usersObj);
                setAllUsers(usersArray);
            }).finally(() => {
            setLoading(false)
        }).catch(error => {
            console.error('Error fetching users:', error);
        });
    }, [classId]);

    // Filter users for the selected class using existing data and split by type
    useEffect(() => {
        if (selectedClass && Object.keys(users).length > 0) {
            if (selectedClass.userIds) {
                const classUsersData = selectedClass.userIds.map(userId => users[userId]).filter(Boolean);

                // Split users by subscriber type
                const students = classUsersData.filter(user => user.subscriberType === 'STUDENT');
                const teachers = classUsersData.filter(user => user.subscriberType === 'TEACHER');

                setClassStudents(students);
                setClassTeachers(teachers);
            } else {
                setClassStudents([]);
                setClassTeachers([]);
            }
        }
    }, [selectedClass, users]);

    const handleChangeTab = (key) => {
        setActiveTabKey(key);
    };

    const handleAddUsersToClass = async (selectedUsers) => {
        if (!selectedUsers.length || !selectedClass) return false;

        try {
            const userIds = selectedUsers.map(item => item.value);
            await axios.post(`/web/ui/sc/addToClass?classId=${selectedClass.classId}`, userIds);

            const addedNames = selectedUsers
                .map(item => {
                    const user = allUsers.find(u => u.userId.toString() === item.value);
                    return user?.fullName;
                })
                .join(', ');

            displayToastMessage(`${addedNames} added to ${selectedClass.className}`, null, 'success');

            const updatedClass = {
                ...selectedClass,
                userIds: [...(selectedClass.userIds || []), ...userIds.map(id => parseInt(id))]
            };
            setSelectedClass(updatedClass);
            setSelectedUsers([]);
            setDrawerVisible(false);

        } catch (error) {
            console.error('Failed to add users to class:', error);
            displayToastMessage('Failed to add users to class', null, 'error');
        }
    };

    const handleRemoveSuccess = (userId) => {
        const updatedClass = {
            ...selectedClass,
            userIds: selectedClass.userIds.filter(id => id !== userId)
        };
        setSelectedClass(updatedClass);
    };

    const renderUserCard = (user, idx, showRemoveButton = false) => (
        <Card key={user.userId || idx} style={{ marginBottom: 8 }} size={"small"}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div className={'ch-manager-users-selector-card-heading'}>
                    <h3>
                        {user.fullName}
                        {user.schoolAdmin &&
                            <Tooltip title={'Admin user'}>
                                <ShieldRounded fontSize={'small'}
                                               style={{marginLeft: '8px', color: '#1890ff'}}/>
                            </Tooltip>
                        }
                        {user.expired &&
                            <Tooltip title={'User is expired'}>
                                <ScheduleRounded fontSize={'small'} style={{marginLeft: '8px', color: '#1890ff'}}/>
                            </Tooltip>
                        }
                    </h3>
                    <p>{user.email} · <span>{user.subscriberType.toLowerCase()}</span></p>
                </div>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                    {showRemoveButton && (
                        <RemoveUserFromClassButton
                            userId={user.userId}
                            classId={selectedClass?.classId}
                            userName={user.fullName}
                            className={selectedClass?.className}
                            onSuccess={handleRemoveSuccess}
                        />
                    )}
                </div>
            </div>
        </Card>
    );

    const renderUserSection = (users, title, emptyMessage) => (
        <div style={{marginBottom: '24px'}}>
            <h4 style={{marginBottom: '12px', color: '#1890ff'}}>{title} ({users.length})</h4>
            <div>
                {users.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                        {emptyMessage}
                    </div>
                ) : (
                    users.map((user, idx) => renderUserCard(user, idx, true))
                )}
            </div>
        </div>
    );

    if (loading) {
        return (<CircularLoader/>)
    }

    return (
        <div className={'ch-drawer-right-content ch-manage-users-wrapper'}>
            <h2>Manage users in {selectedClass?.className}</h2>
            <Divider className={'ch-divider'}/>

            <Tabs style={{position: 'sticky', top: 0}} overrideKey={activeTabKey} variant="standard" onChange={handleChangeTab} contentClassName="margin-t--l" destroyInactiveTabPane={false}>
                <Tab
                    tab={<span className={'tabText'}>{TABS.EXISTING_USERS.label}</span>}
                    key={TABS.EXISTING_USERS.val}
                >
                    <div style={{borderBottom: '1px solid lightgrey'}}>
                        {renderUserSection(classStudents, 'Students', 'No students found in this class.')}
                        {renderUserSection(classTeachers, 'Teachers', 'No teachers found in this class.')}
                    </div>
                </Tab>
                <Tab
                    tab={<span className={'tabText'}>{TABS.ADD_USERS.label}</span>}
                    key={TABS.ADD_USERS.val}
                >
                    <UserSelector
                        selectedUsers={selectedUsers}
                        onSelectedUsersChange={setSelectedUsers}
                        onAddUsers={handleAddUsersToClass}
                        showAddButton={true}
                        existingUserIds={selectedClass?.userIds || []}
                    />
                </Tab>
            </Tabs>
        </div>
    );
};

export default ManageClassUsersDrawer;